import React, { memo, useImperativeHandle, useRef, forwardRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import { RadioGroup as BaseRadioGroup } from '../Controls'
import { handleRequired, ErrorMessage, useFormBase, formImperativeHandleFunctions, handleFormPostObject } from './common'
import { translate } from '../../multilanguage'
import { FormFieldContainer } from './constants'
import { useTranslation } from 'react-i18next'

/**
 * Implements Mui RadioGroup for forms.
 *
 * @param {object} props component props
 * @returns {RadioGroup} RadioGroup component
 */
const RadioGroup = memo(forwardRef((props, ref) => {
  const { t } = useTranslation()
  const { fixedErrorMessageFieldHeight, fieldTemplate, data, items, label, ...finalProps } = props
  const [radioItems, setSelectItems] = useState(items)
  const originalItems = useRef(items)

  const { onChange, errorMsgFixedHeight, disabled, setDisabled, fieldLabel, refFormElement, valueRef, errorMsg, setErrorMsg } = 
    useFormBase(props, fieldTemplate, data)

  useEffect(() => {
    if (items.length > 0) {
      originalItems.current = [...items]
    }
    setSelectItems(items)
  }, [items])

  useImperativeHandle(ref, () => ({
    ...formImperativeHandleFunctions(fieldTemplate, refFormElement, valueRef, setErrorMsg, setDisabled),
    validate: (postObject, refs) => {
      const value = valueRef.current
      let error = ''

      if (fieldTemplate.isRequired && !value) {
        error = handleRequired(props)
      } else if (fieldTemplate.validator) {
        error = translate(fieldTemplate.validator(value))
      }

      return handleFormPostObject(postObject, fieldTemplate, value, refs, error, setErrorMsg)
    }
  }))

  return (
    <FormFieldContainer>
      <BaseRadioGroup
        {...finalProps}
        disabled={finalProps.disabled !== undefined ? finalProps.disabled : disabled}
        items={radioItems}
        ref={refFormElement}
        onChange={onChange}
        error={!!errorMsg}
        label={t(`${fieldLabel.current}`)}
        noTranslate={true}
      />
      <ErrorMessage errorMsg={errorMsg} errorMsgFixedHeight={errorMsgFixedHeight} />
    </FormFieldContainer>
  )
}))

RadioGroup.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Initial value for the select.
   */
  defaultValue: PropTypes.string,
  /**
   Label to display for the control.
   */ 
  label: PropTypes.string,
  /**
   onChange function called when the Select is clicked.
   */
  onChange: PropTypes.func,
  /**
   Items to display in the radio group.
   */
  items: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })).isRequired,
  /**
   Prevents the language translation of the label.
   */
  noTranslate: PropTypes.bool,
  /**
   Prevents the language translation of the items labels.
   */
  noTranslateItems: PropTypes.bool,
  /**
   Style for the control.
   */
  style: PropTypes.object,
  /**
   MUI variant for the control.
   */
  variant: PropTypes.oneOf(['outlined', 'standard']),
  /**
   Determines if the control is disabled.
   */
  disabled: PropTypes.bool,
  /**
   Puts the control in an error state.
   */
  error: PropTypes.bool,
  /**
   Adds a fixed height at the bottom of the control for error messages.
   */
  fixedErrorMessageFieldHeight: PropTypes.bool,
  /**
   Field definition template for the control.
   */
  fieldTemplate: PropTypes.object.isRequired,
  /**
   The data object retrieved from the backend which contains all data for the form controls.
   */
  data: PropTypes.object,
  /**
   Set a ref to the component in your page and call this function to restore its default value contained in the fieldTemplate prop.
   */
  'ref.current.clear': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to get its current value.
   */
  'ref.current.getValue': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to set its current value.
   */
  'ref.current.setValue': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to run the validators in the fieldTemplate prop.
   */
  'ref.current.validate': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to disable the control.
   */
  'ref.current.setDisabled': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to set the error message for the control.
   */
  'ref.current.setErrorMsg': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to clear the error message for the control.
   */
  'ref.current.clearErrorMsg': PropTypes.func,
  /**
   Returns the form template associated with this form control.
   */
  'ref.current.getFormTemplate': PropTypes.func,
}

RadioGroup.defaultProps = {
  noTranslate: false,
  noTranslateItems: false
}

export default RadioGroup